import { SuretyQuoteStatus, stripEmpty } from "@inrev/common";
import { useFetchBondFormTemplates } from "@portal/api/bond-request";
import {
	type BondRequest,
	useFetchQuoteBondForm,
	useUpdateBondForm,
} from "@portal/api/bond-request/agent";
import {
	getAllowedBondFormUploadTypes,
	getFilteredBondFormTemplates,
} from "@portal/views/shared/bond/bondUtils";
import { useMemo } from "react";
import type { BondFormData } from "../../../types";
import { BondFormSection } from "./BondFormSection";
import { useBondForm } from "./useBondForm";

const isDeleteAllowed = (status: BondRequest["status"]) =>
	status !== SuretyQuoteStatus.pending && status !== SuretyQuoteStatus.closed;

const isUploadAllowed = (status: BondRequest["status"]) =>
	status === SuretyQuoteStatus.accepted ||
	status === SuretyQuoteStatus.approved ||
	status === SuretyQuoteStatus.review;

const isBondFormRequired = (status: BondRequest["status"]) => status === SuretyQuoteStatus.accepted;

export const AgentBondFormSection = ({ request }: { request: BondRequest }) => {
	const { updateBondForm } = useUpdateBondForm(request.suretyAccountId, request.id);
	const { quoteBondForm, quoteBondFormIsLoading } = useFetchQuoteBondForm(
		request.suretyAccountId,
		request.id,
	);
	const { bondFormTemplates: templates, bondFormTemplatesIsLoading } = useFetchBondFormTemplates();

	const filteredTemplates = useMemo(
		() => getFilteredBondFormTemplates(templates, stripEmpty(request.contractSuretyType)),
		[templates, request.contractSuretyType],
	);

	const typeLabels = useMemo(
		() => getAllowedBondFormUploadTypes(stripEmpty(request.contractSuretyType)),
		[request.contractSuretyType],
	);

	const savedBondForm = useMemo<BondFormData | undefined>(() => {
		if (!quoteBondForm) return undefined;
		if (quoteBondForm.templateId) {
			return { templateId: quoteBondForm.templateId, upload: { type: "", files: [] } };
		}
		if (quoteBondForm.upload) {
			return { templateId: "", upload: quoteBondForm.upload };
		}
		return { templateId: "", upload: { type: "", files: [] } };
	}, [quoteBondForm]);

	const { value, onChange } = useBondForm({ savedBondForm, submit: updateBondForm });

	const isEmpty = value.templateId === "" && value.upload.files.length === 0;

	return (
		<BondFormSection
			value={value}
			onChange={onChange}
			templates={filteredTemplates}
			typeLabels={typeLabels}
			allowDelete={isDeleteAllowed(request.status)}
			allowUpload={isUploadAllowed(request.status)}
			partial={!isBondFormRequired(request.status) && isEmpty}
			isLoading={quoteBondFormIsLoading || bondFormTemplatesIsLoading}
		/>
	);
};
